// Rótulos, cores e textos do Programa de Indicações (#6196). Lógica pura: a tela só
// desenha o que sai daqui.
import type {
  IndicacaoAndamento,
  IndicacaoCanal,
  IndicacaoChecagem,
  IndicacaoMotivoNaoVale,
  IndicacaoResultadoAbordagem,
  RecompensaStatus,
  RecompensaTipo,
} from '../../../types';

export const ROTULO_CANAL: Record<IndicacaoCanal, string> = {
  ligacao: 'Ligação',
  whatsapp: 'WhatsApp',
  visita: 'Visita',
};

export const ROTULO_RESULTADO: Record<IndicacaoResultadoAbordagem, string> = {
  indicou: 'Indicou',
  nao_quis: 'Não quis indicar',
  voltar_depois: 'Pediu para voltar depois',
  nao_atendeu: 'Não atendeu',
};

export const ROTULO_MOTIVO: Record<IndicacaoMotivoNaoVale, string> = {
  ja_cliente: 'já é cliente da Vigor',
  negociacao_ativa: 'já estava em negociação',
  ja_na_base: 'já estava na base da Vigor',
};

export const ROTULO_TIPO_RECOMPENSA: Record<RecompensaTipo, string> = {
  dinheiro: 'Dinheiro (PIX)',
  desconto_fatura: 'Desconto na fatura',
  brinde: 'Brinde',
};

export interface Estilo {
  rotulo: string;
  classe: string;
}

const CINZA = 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300';
const AZUL = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
const VERDE = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
const AMBAR = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
const VERMELHO = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
const MARCA = 'bg-primary/15 text-primary dark:bg-primary/30 dark:text-lime-200';

export const ANDAMENTO: Record<IndicacaoAndamento, Estilo> = {
  em_negociacao: { rotulo: 'Em negociação', classe: AZUL },
  aprovada: { rotulo: 'Aprovada', classe: VERDE },
  recompensa_paga: { rotulo: 'Recompensa paga', classe: MARCA },
  perdida: { rotulo: 'Perdida', classe: CINZA },
  nao_vale: { rotulo: 'Não vale', classe: AMBAR },
  negociacao_removida: { rotulo: 'Negociação removida', classe: VERMELHO },
};

export const RECOMPENSA_STATUS: Record<RecompensaStatus, Estilo> = {
  a_pagar: { rotulo: 'A pagar', classe: AMBAR },
  suspensa: { rotulo: 'Suspensa', classe: CINZA },
  paga: { rotulo: 'Paga', classe: VERDE },
  cancelada: { rotulo: 'Cancelada', classe: VERMELHO },
};

export const SELO_CLIENTE: { sim: Estilo; nao: Estilo } = {
  sim: { rotulo: 'Cliente', classe: MARCA },
  nao: { rotulo: 'Não cliente', classe: CINZA },
};

const FORMATO_DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const FORMATO_DATA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
});
const FORMATO_MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const paraData = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "15/10/2026 10:42", sempre no horário de Brasília. */
export function formatarDataHora(iso?: string | null): string {
  const d = paraData(iso);
  return d ? FORMATO_DATA_HORA.format(d).replace(',', '') : '';
}

export function formatarData(iso?: string | null): string {
  const d = paraData(iso);
  return d ? FORMATO_DATA.format(d) : '';
}

/** Valor nulo = recompensa ainda sem valor no programa. */
export function formatarMoeda(v?: number | null): string {
  return v == null ? 'a definir' : FORMATO_MOEDA.format(v);
}

export function primeiroNome(nome?: string | null): string {
  return (nome ?? '').trim().split(/\s+/)[0] ?? '';
}

export function mensagemChecagem(c: IndicacaoChecagem): { tom: 'ok' | 'aviso' | 'erro'; texto: string } {
  switch (c.motivo) {
    case 'whatsapp_invalido':
      return { tom: 'erro', texto: 'WhatsApp inválido.' };
    case 'auto_indicacao':
      return { tom: 'erro', texto: 'A pessoa não pode indicar a si mesma.' };
    case 'ja_cliente':
      return { tom: 'aviso', texto: 'Não vale: já é cliente da Vigor.' };
    case 'negociacao_ativa':
      return {
        tom: 'aviso',
        texto: c.conflito?.responsavel_nome
          ? `Não vale: já está no CRM com ${c.conflito.responsavel_nome}.`
          : 'Não vale: já está em negociação.',
      };
    case 'ja_na_base':
      return { tom: 'aviso', texto: 'Não vale: já está na base da Vigor.' };
    default:
      if (c.anterior) {
        return {
          tom: 'ok',
          texto: c.anterior.status === 'termo_reprovado'
            ? `Vale. Atenção: foi reprovado na análise em ${formatarData(c.anterior.em)}.`
            : 'Vale. Já teve uma negociação perdida; uma nova será aberta.',
        };
      }
      return { tom: 'ok', texto: 'Vale: vira negociação nova.' };
  }
}

export interface ResumoEntrada {
  abordagens: number;
  ultima_abordagem_em: string | null;
  ultima_abordagem_por: string | null;
  indicacoes: number;
  aprovadas: number;
}

/** "Abordada 2x · última 15/10/2026 10:42 por Dhaiane · 3 indicações · 1 aprovada". */
export function resumoPessoa(r: ResumoEntrada): string {
  if (r.abordagens === 0 && r.indicacoes === 0) return 'Nunca abordada';
  const partes: string[] = [];
  if (r.abordagens > 0) partes.push(`Abordada ${r.abordagens}x`);
  if (r.ultima_abordagem_em) {
    const por = r.ultima_abordagem_por ? ` por ${primeiroNome(r.ultima_abordagem_por)}` : '';
    partes.push(`última ${formatarDataHora(r.ultima_abordagem_em)}${por}`);
  }
  partes.push(`${r.indicacoes} ${r.indicacoes === 1 ? 'indicação' : 'indicações'}`);
  partes.push(`${r.aprovadas} ${r.aprovadas === 1 ? 'aprovada' : 'aprovadas'}`);
  return partes.join(' · ');
}
